import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Spinner } from 'react-bootstrap';
import { requestPasswordReset, resetPassword } from '../../redux/thunk/authThunk';
import { setError, setShowLoginModal, setShowForgotPasswordModal } from '../../redux/slices/authSlice';
import { MixGoatWordmark } from '../MixGoatLogo';
import { useDesignV2 } from '../../utils/designV2';
import './ForgotPasswordModal.scss';

const ForgotPasswordModal = () => {
    const dispatch = useDispatch();
    const { isLoading, error, showForgotPasswordModal } = useSelector((state) => state.auth);
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [success, setSuccess] = useState(false);
    const v2 = useDesignV2(); // v2 copy is sentence case

    useEffect(() => {
        if (showForgotPasswordModal) {
            const saved = sessionStorage.getItem('forgotPasswordEmail');
            if (saved) {
                setEmail(saved);
                sessionStorage.removeItem('forgotPasswordEmail');
            }
        }
    }, [showForgotPasswordModal]);

    if (!showForgotPasswordModal) return null;

    const switchToLogin = () => {
        dispatch(setError(null));
        setStep(1);
        setEmail('');
        setCode('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccess(false);
        dispatch(setShowForgotPasswordModal(false));
        dispatch(setShowLoginModal(true));
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            dispatch(setError('Please enter your email'));
            return;
        }

        const result = await dispatch(requestPasswordReset(email.trim()));
        if (result.payload?.success) {
            setStep(2);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!code.trim()) {
            dispatch(setError('Please enter the reset code'));
            return;
        }
        if (!newPassword) {
            dispatch(setError('Please enter a new password'));
            return;
        }
        if (newPassword.length < 8) {
            dispatch(setError('Password must be at least 8 characters'));
            return;
        }
        if (newPassword !== confirmPassword) {
            dispatch(setError('Passwords do not match'));
            return;
        }

        const result = await dispatch(resetPassword({
            email: email.trim(),
            code: code.trim(),
            newPassword,
        }));
        if (result.payload?.success) {
            setSuccess(true);
        }
    };

    return (
        <div className="auth-overlay">
            <div className="auth-container">
                <div className="auth-logo">
                    <MixGoatWordmark height={60} />
                </div>

                <h2 className="auth-title">{v2 ? 'Reset your password' : 'Reset Password'}</h2>

                {success ? (
                    <div className="auth-success-message">
                        <p>Password reset successfully!</p>
                        <p>You can now log in with your new password.</p>
                        <Button
                            className="auth-submit-btn"
                            onClick={switchToLogin}
                            style={{ marginTop: '20px' }}
                        >
                            {v2 ? 'Back to log in' : 'Back to Login'}
                        </Button>
                    </div>
                ) : step === 1 ? (
                    <>
                        <p className="auth-subtitle">
                            Enter your email and we'll send you a 6-digit code to reset your password.
                        </p>

                        {error && (
                            <div className="auth-alert">
                                <span>{error}</span>
                                <button className="auth-alert-close" onClick={() => dispatch(setError(null))}>×</button>
                            </div>
                        )}

                        <Form onSubmit={handleSendCode} className="auth-form" noValidate>
                            <Form.Group className="auth-field">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); if (error) dispatch(setError(null)); }}
                                    disabled={isLoading}
                                    autoFocus
                                />
                            </Form.Group>

                            <Button
                                type="submit"
                                className="auth-submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Sending...
                                    </>
                                ) : (
                                    (v2 ? 'Send reset code' : 'Send Reset Code')
                                )}
                            </Button>
                        </Form>

                        <div className="auth-footer">
                            <span className="auth-footer-text">
                                <button
                                    className="auth-link"
                                    onClick={switchToLogin}
                                    disabled={isLoading}
                                >
                                    {v2 ? 'Back to log in' : 'Back to Login'}
                                </button>
                            </span>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="auth-subtitle">
                            Enter the code sent to <strong>{email}</strong> and your new password.
                        </p>

                        {error && (
                            <div className="auth-alert">
                                <span>{error}</span>
                                <button className="auth-alert-close" onClick={() => dispatch(setError(null))}>×</button>
                            </div>
                        )}

                        <Form onSubmit={handleResetPassword} className="auth-form" noValidate>
                            <input type="email" value={email} autoComplete="username" readOnly hidden />
                            <Form.Group className="auth-field">
                                <Form.Label>{v2 ? 'Reset code' : 'Reset Code'}</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="Reset code"
                                    value={code}
                                    onChange={(e) => { setCode(e.target.value); if (error) dispatch(setError(null)); }}
                                    disabled={isLoading}
                                    autoFocus
                                    maxLength={8}
                                    autoComplete="one-time-code"
                                    className="reset-code-input"
                                />
                            </Form.Group>

                            <Form.Group className="auth-field">
                                <Form.Label>{v2 ? 'New password' : 'New Password'}</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Min. 8 characters"
                                    value={newPassword}
                                    onChange={(e) => { setNewPassword(e.target.value); if (error) dispatch(setError(null)); }}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                />
                            </Form.Group>

                            <Form.Group className="auth-field">
                                <Form.Label>{v2 ? 'Confirm password' : 'Confirm Password'}</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={(e) => { setConfirmPassword(e.target.value); if (error) dispatch(setError(null)); }}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                />
                            </Form.Group>

                            <Button
                                type="submit"
                                className="auth-submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Resetting...
                                    </>
                                ) : (
                                    (v2 ? 'Reset password' : 'Reset Password')
                                )}
                            </Button>
                        </Form>

                        <div className="auth-footer">
                            <span className="auth-footer-text">
                                <button
                                    className="auth-link"
                                    onClick={switchToLogin}
                                    disabled={isLoading}
                                >
                                    {v2 ? 'Back to log in' : 'Back to Login'}
                                </button>
                            </span>
                        </div>
                    </>
                )}

                <p className="auth-legal">
                    Official MixGoat password reset. By continuing you agree to our{' '}
                    <button
                        type="button"
                        className="auth-link"
                        onClick={() => window.electron.openUrl('https://mixgoat.com/terms')}
                    >
                        Terms
                    </button>
                    {' '}and{' '}
                    <button
                        type="button"
                        className="auth-link"
                        onClick={() => window.electron.openUrl('https://mixgoat.com/privacy')}
                    >
                        Privacy Policy
                    </button>.
                </p>
            </div>
        </div>
    );
};

export default ForgotPasswordModal;
